import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

interface CampaignListState {
  campaignList: GroupedCampaigns | null;

  getCampaignList: (query?: Array<QueryParams>) => Promise<void>;
  deleteCampaign: (campaingId: string) => Promise<void>;
  recoverCampaign: (campaingId: string) => Promise<void>;
}

export interface Campaign {
  id: string;
  project_code: string;
  name: string;
  status: string;
  progress_status: string;
  header_image?: {
    id: string | null;
    key: string | null;
    mime: string | null;
    type: string;
    url: string | null;
  };
  status_counts?: {
    briefed: number;
    in_progress: number;
    in_review: number;
    approved: number;
    trafficking: number;
    live: number;
    draft: number;
    total: number;
  };
}

export interface GroupedCampaigns {
  [groupName: string]: Campaign[];
}

interface CampaignsResponse {
  success: boolean;
  message: string;
  data: GroupedCampaigns;
}

interface DeleteCampaignsResponse {
  success: boolean;
  message: string;
}

interface QueryParams {
  key: string;
  value: string;
}

const useCampaignListStore = create<CampaignListState>((set, get) => ({
  campaignList: {},

  getCampaignList: async (query?: Array<QueryParams>) => {
    try {
      let params: { [key: string]: string | number | boolean } = {};
      if (query && query.length > 0) {
        query.forEach((q) => {
          params[q.key] = q.value;
        });
      }

      const response = await apiClient.get<CampaignsResponse>(
        "/grouped/by-client-teams",
        {
          params: params,
        }
      );

      const data = response.data.data;
      set({ campaignList: data });
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  recoverCampaign: async (campaignId) => {
    try {
      const response = await apiClient.post<DeleteCampaignsResponse>(
        `/campaigns/actions/restore`,
        {
          id: campaignId,
        }
      );

      set((state) => {
        const updatedGroupedCampaigns: typeof state.campaignList = {};

        for (const [groupName, campaigns] of Object.entries(
          state.campaignList
        )) {
          const filteredCampaigns = campaigns.filter(
            (c) => c.id !== campaignId
          );
          updatedGroupedCampaigns[groupName] = filteredCampaigns;
        }

        return { campaignList: updatedGroupedCampaigns };
      });
    } catch (error) {
      console.error(error);
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteCampaign: async (campaignId) => {
    try {
      const response = await apiClient.delete<DeleteCampaignsResponse>(
        `/campaigns/${campaignId}`
      );

      set((state) => {
        const updatedGroupedCampaigns: typeof state.campaignList = {};

        for (const [groupName, campaigns] of Object.entries(
          state.campaignList
        )) {
          const filteredCampaigns = campaigns.filter(
            (c) => c.id !== campaignId
          );
          updatedGroupedCampaigns[groupName] = filteredCampaigns;
        }

        return { campaignList: updatedGroupedCampaigns };
      });
    } catch (error) {
      console.error(error);
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useCampaignListStore;
